import NewTooltip from '../../../atoms/NewTooltip';

interface Branch {
  readonly id: string;
  readonly name: string;
  readonly document: string;
  readonly role?: string;
  readonly city?: string;
  readonly state?: string;
}

interface Props {
  readonly branches: Branch[];
  readonly shareEnabled: boolean;
  readonly selectedBranches: Set<string>;
  readonly onToggleShare: (enabled: boolean) => void;
  readonly onToggleBranch: (branchId: string) => void;
  readonly onToggleAll: () => void;
}

export default function ShareBranchSelector({ branches, shareEnabled, selectedBranches, onToggleShare, onToggleBranch, onToggleAll }: Props) {
  return (
    <div className="space-y-3">
      <label className="flex items-center gap-3 cursor-pointer">
        <div className="relative">
          <input
            type="checkbox"
            checked={shareEnabled}
            onChange={(e) => onToggleShare(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 dark:bg-slate-600 rounded-full peer peer-checked:bg-purple-500 peer-focus:ring-2 peer-focus:ring-purple-300 transition-colors"></div>
          <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
        </div>
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Compartilhar com empresas do grupo
        </span>
      </label>

      {shareEnabled && (
        <BranchList
          branches={branches}
          selectedBranches={selectedBranches}
          onToggleBranch={onToggleBranch}
          onToggleAll={onToggleAll}
        />
      )}
    </div>
  );
}

function BranchList({ branches, selectedBranches, onToggleBranch, onToggleAll }: {
  readonly branches: Branch[];
  readonly selectedBranches: Set<string>;
  readonly onToggleBranch: (branchId: string) => void;
  readonly onToggleAll: () => void;
}) {
  return (
    <div className="border border-purple-200 dark:border-purple-800 rounded-lg p-3 space-y-2 bg-purple-50 dark:bg-purple-900/20 min-w-[500px]">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-gray-500 dark:text-gray-400">Selecione as empresas que receberão este contato:</p>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={selectedBranches.size === branches.length && branches.length > 0}
            onChange={onToggleAll}
            className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
          />
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Selecionar todas</span>
        </label>
      </div>
      <div className="max-h-40 overflow-y-auto space-y-1">
        {branches.map((branch) => (
          <BranchItem key={branch.id} branch={branch} selected={selectedBranches.has(branch.id)} onToggle={() => onToggleBranch(branch.id)} />
        ))}
      </div>
    </div>
  );
}

function BranchItem({ branch, selected, onToggle }: { readonly branch: Branch; readonly selected: boolean; readonly onToggle: () => void }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer py-1">
      <input type="checkbox" checked={selected} onChange={onToggle} className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500 flex-shrink-0" />
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <NewTooltip content={branch.name} alignment="top">
            <span className="text-sm text-gray-900 dark:text-white truncate max-w-[220px] inline-block align-bottom">{branch.name}</span>
          </NewTooltip>
          <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">({branch.document})</span>
          {branch.role && <span className="text-xs text-purple-500 dark:text-purple-400 flex-shrink-0">{branch.role}</span>}
        </div>
        {(branch.city || branch.state) && <div className="text-xs text-gray-400 dark:text-gray-500">{[branch.city, branch.state].filter(Boolean).join(' - ')}</div>}
      </div>
    </label>
  );
}
